import { useNavigate, useParams } from 'react-router-dom'
import useData from '../../../common/hooks/useData'
import TasksList from '../../admin/tasks/TasksList/TasksList'
import Spinner from '../../../components/common/Spinner'
import ErrorMessage from '../../../components/common/ErrorMessage'
import SectionContainer from '../../../components/common/SectionContainer'
import { ICONS, TASK_STATUSES } from '../../../common/constants'
import MainButton from '../../../components/admin/MainButton'
import Preformatted from '../../../components/common/Preformatted'
import BlockStack from '../../../components/common/BlockStack'
import InlineStack from '../../../components/common/InlineStack'
import ActionsDropdown from '../../../components/common/ActionsDropdown'
import useModal from '../../../common/hooks/useModal'
import useToast from '../../../common/hooks/useToast'
import useMatchMutate from '../../../common/hooks/useMatchMutate'
import ConfirmModalContent from '../../../components/common/ConfirmModalContent'
import fetchAPI from '../../../common/fetchAPI'

export default function ProjectPage() {
    const { projectId } = useParams()
    const navigate = useNavigate()
    const { setModal, setModalLoading, isModalLoading } = useModal()
    const setToast = useToast()
    const matchMutate = useMatchMutate()

    const { project, projectLoading, projectError } = useData(
        `/v1/tasks/projects/${projectId}?fields[]=title&fields[]=descriptionHtml`,
        'project',
        (data) => data,
    )

    function handleDelete() {
        setModal(
            <ConfirmModalContent
                onConfirm={async () => {
                    if (isModalLoading) return
                    setModalLoading(true)

                    const { error } = await fetchAPI(
                        `/v1/tasks/projects/${project.id}`,
                        {},
                        'DELETE',
                    )

                    setModal(null)
                    setModalLoading(false)

                    if (error) {
                        setToast(error, 'alert')
                        return
                    }

                    setToast('Project deleted')
                    matchMutate(/\/v1\/tasks\/projects/)
                    navigate('/projects')
                }}
            />,
            'Are you sure you want to delete this project?',
        )
    }

    if (projectLoading) {
        return <Spinner />
    }

    if (projectError) {
        return (
            <SectionContainer>
                <ErrorMessage>{projectError}</ErrorMessage>
            </SectionContainer>
        )
    }

    return (
        <>
            <SectionContainer>
                <header className="info-header">
                    <BlockStack gap="sm">
                        <InlineStack spaceBetween gap="sm">
                            <div>
                                <h2>{project.title}</h2>
                                {project.descriptionHtml && (
                                    <Preformatted>
                                        <div
                                            // className={`task-description-${task.id}`}
                                            dangerouslySetInnerHTML={{
                                                __html: project.descriptionHtml,
                                            }}
                                        ></div>
                                    </Preformatted>
                                )}
                            </div>
                            <ActionsDropdown
                                actions={[
                                    {
                                        title: 'Edit',
                                        onClick: () =>
                                            navigate(
                                                `/projects/${project.id}/edit`,
                                            ),
                                        icon: ICONS.EDIT_GRAY,
                                    },
                                    {
                                        title: 'Delete',
                                        onClick: handleDelete,
                                        icon: ICONS.TRASH_RED,
                                        type: 'alert',
                                    },
                                ]}
                            />
                        </InlineStack>
                    </BlockStack>
                </header>
            </SectionContainer>

            <SectionContainer>
                <MainButton
                    href={`/projects/${projectId}/edit`}
                    functionality="EDIT"
                    label="Edit Project"
                />

                <TasksList
                    title={'Tasks'}
                    renderMainButton={true}
                    newItemLabel={'New task'}
                    newItemPath={`/tasks/new?projectId=${projectId}`}
                    projectId={projectId}
                    apiUrl={'/v1/tasks'}
                    paginationLimit={100}
                    statusTabs={[
                        TASK_STATUSES.REVIEW,
                        TASK_STATUSES.REJECTED,
                        TASK_STATUSES.ACCEPTED,
                        TASK_STATUSES.OPEN,
                        TASK_STATUSES.DENIED,
                        TASK_STATUSES.APPROVAL,
                        TASK_STATUSES.RECREATION,
                        TASK_STATUSES.BLOCKED,
                        TASK_STATUSES.HOLD,
                        TASK_STATUSES.COMPLETE,
                        TASK_STATUSES.CANCELLED,
                    ]}
                    getGroup={(item) =>
                        item.project?.phases?.find(
                            (p) => (p._id || p.id) === item.projectPhaseId,
                        )?.title || ''
                    }
                />
            </SectionContainer>
        </>
    )
}
